// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ImgProps } from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import fieldDefinitionBuilder from "@store/Framework/Features/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { FieldDefinitionBuilderFields } from "@store/Framework/Features/FieldDefinitionBuilder/FieldDefinitionBuilderTypes";
import { responsiveCss } from "@store/Framework/Features/FieldDefinitionBuilder/Utilities/responsiveCss";
import { fullViewportHeight } from "@store/Framework/Common/Utilities/siteBanners";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    hasImageOverlay = "hasImageOverlay",
    imageOverlayType = "imageOverlayType",
    imageOverlayColor = "imageOverlayColor",
    backgroundImagePositionX = "backgroundImagePositionX",
    backgroundImagePositionY = "backgroundImagePositionY",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    minimumHeight = "minimumHeight",
    customMinHeight = "customMinHeight",
    maxWidth = "maxWidth",
    customMaxWidth = "customMaxWidth",
    verticalAligment = "verticalAligment",
    horizontalAligment = "horizontalAligment",
    textAligment = "textAligment",
    hasBorder = "hasBorder",
    borderColor = "borderColor",
    borderWidth = "borderWidth",
    isBorderWidthAdvancedOptions = "isBorderWidthAdvancedOptions",
    borderWidthTop = "borderWidthTop",
    borderWidthRight = "borderWidthRight",
    borderWidthBottom = "borderWidthBottom",
    borderWidthLeft = "borderWidthLeft",
    borderRadius = "borderRadius",
    isBorderRadiusAdvancedOptions = "isBorderRadiusAdvancedOptions",
    borderRadiusTopLeft = "borderRadiusTopLeft",
    borderRadiusTopRight = "borderRadiusTopRight",
    borderRadiusBottomRight = "borderRadiusBottomRight",
    borderRadiusBottomLeft = "borderRadiusBottomLeft",
    contentPadding = "contentPadding",
    isPaddingAdvancedOptions = "isPaddingAdvancedOptions",
    paddingTop = "paddingTop",
    paddingRight = "paddingRight",
    paddingBottom = "paddingBottom",
    paddingLeft = "paddingLeft",
    testSelectorId = "testSelectorId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.hasImageOverlay]: boolean;
        [fields.imageOverlayType]: "plain" | "gradient";
        [fields.imageOverlayColor]: string;
        [fields.backgroundImagePositionX]: number;
        [fields.backgroundImagePositionY]: number;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.minimumHeight]: "None" | "1/4" | "1/2" | "3/4" | "full" | "custom";
        [fields.customMinHeight]: number;
        [fields.maxWidth]: "fullWidth" | "fitContent" | "custom";
        [fields.customMaxWidth]: number;
        [fields.verticalAligment]: "flex-start" | "center" | "flex-end";
        [fields.horizontalAligment]: "flex-start" | "center" | "flex-end";
        [fields.textAligment]: "left" | "center" | "right";
        [fields.hasBorder]: boolean;
        [fields.borderColor]: string;
        [fields.borderWidth]: number;
        [fields.isBorderWidthAdvancedOptions]: boolean;
        [fields.borderWidthTop]: number;
        [fields.borderWidthRight]: number;
        [fields.borderWidthBottom]: number;
        [fields.borderWidthLeft]: number;
        [fields.borderRadius]: number;
        [fields.isBorderRadiusAdvancedOptions]: boolean;
        [fields.borderRadiusTopLeft]: number;
        [fields.borderRadiusTopRight]: number;
        [fields.borderRadiusBottomRight]: number;
        [fields.borderRadiusBottomLeft]: number;
        [fields.contentPadding]: number;
        [fields.isPaddingAdvancedOptions]: boolean;
        [fields.paddingTop]: number;
        [fields.paddingRight]: number;
        [fields.paddingBottom]: number;
        [fields.paddingLeft]: number;
        [fields.testSelectorId]?: string;
    } & FieldDefinitionBuilderFields;
}

export interface StyledContainerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    logo?: ImgProps;
}

export const styledContainerStyles: StyledContainerStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: stretch;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    logo: {
        css: css`
            position: absolute;
            left: 50%;
            right: 50%;
            transform: translateX(-50%);
        `,
    },
};

const styles = styledContainerStyles;

export const StyledContainer: FC<OwnProps> = ({ id, fields }) => {
    const isEditing = useContext(ShellContext).isEditing || false;
    const backgroundStyles = responsiveCss(fields, field => {
        return css`
            ${field.background === "image"
                ? css`
                      background-color: transparent;
                      background-image: url("${field.image}");
                      background-size: cover;
                      background-position: center;
                  `
                : null}
            ${field.background === "color"
                ? css`
                      background-color: ${field.backgroundColor};
                      background-size: auto;
                      background-image: none;
                  `
                : null}
        `;
    });

    const resolveMinHeight = () => {
        switch (fields.minimumHeight) {
            case "None":
                return "none";
            case "1/4":
                return "25vh";
            case "1/2":
                return "50vh";
            case "3/4":
                return "75vh";
            case "full":
                return fullViewportHeight;
            case "custom":
                return `${fields.customMinHeight}vh`;
        }
    };

    const minimumHeightStyles = css`
        min-height: ${resolveMinHeight()};
    `;

    let widthStyles = css`
        width: 100%;
    `;

    if (fields.maxWidth === "fitContent") {
        widthStyles = css`
            width: auto;
        `;
    }

    if (fields.maxWidth === "custom") {
        widthStyles = css`
            width: 100%;
            max-width: ${pxToRem(fields.customMaxWidth)};
        `;
    }

    const borderWidth = responsiveCss(fields, field => {
        return css`
            ${fields.hasBorder
                ? css`
                      border-style: solid;
                      border-color: ${field.borderColor};

                      ${fields.isBorderWidthAdvancedOptions
                          ? css`
                                border-top-width: ${pxToRem(field.borderWidthTop)};
                                border-right-width: ${pxToRem(field.borderWidthRight)};
                                border-bottom-width: ${pxToRem(field.borderWidthBottom)};
                                border-left-width: ${pxToRem(field.borderWidthLeft)};
                            `
                          : css`
                                border-width: ${pxToRem(field.borderWidth)};
                            `}
                  `
                : null}
        `;
    });

    const borderRadius = responsiveCss(fields, field => {
        return css`
            ${fields.isBorderRadiusAdvancedOptions
                ? css`
                      border-top-left-radius: ${pxToRem(field.borderRadiusTopLeft)};
                      border-top-right-radius: ${pxToRem(field.borderRadiusTopRight)};
                      border-bottom-right-radius: ${pxToRem(field.borderRadiusBottomRight)};
                      border-bottom-left-radius: ${pxToRem(field.borderRadiusBottomLeft)};
                  `
                : css`
                      border-radius: ${pxToRem(field.borderRadius)};
                  `}
        `;
    });

    const layoutStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${minimumHeightStyles}
            ${widthStyles}
            ${borderRadius}
            ${borderWidth}
        `,
    };

    const fieldsPadding = responsiveCss(fields, field => {
        return css`
            padding: ${field.isPaddingAdvancedOptions
                ? `${pxToRem(field.paddingTop)} ${pxToRem(field.paddingRight)} ${pxToRem(
                      field.paddingBottom,
                  )} ${pxToRem(field.paddingLeft)}`
                : pxToRem(field.contentPadding)};
        `;
    });

    const responsiveOverlayWrapperStyles = responsiveCss(fields, field => {
        return css`
            ${field.background === "image" && field.hasImageOverlay
                ? css`
                      ${field.imageOverlayType === "plain" &&
                      css`
                          background-color: ${field.imageOverlayColor};
                      `}

                      ${field.imageOverlayType === "gradient" &&
                      css`
                          background: linear-gradient(-90deg, rgba(32, 56, 50, 0) 0%, ${field.imageOverlayColor} 100%);
                      `}
                  `
                : null}
        `;
    });

    const overlayWrapperStyles = {
        css: css`
            ${!isEditing
                ? css`
                      display: flex;
                  `
                : null}
            ${styles.overlayWrapper?.css}
            ${fieldsPadding}
            ${borderRadius}
            ${responsiveOverlayWrapperStyles}
            align-items: ${fields.verticalAligment};
            justify-content: ${fields.horizontalAligment};
            color: ${fields.textColor};
            text-align: ${fields.textAlignemt};
        `,
    };

    const slideCenteringWrapperStyles = {
        ...styles.slideCenteringWrapperStyles,
        css: css`
            ${styles.slideCenteringWrapperStyles?.css}
            ${widthStyles}
        `,
    };

    return (
        <StyledWrapper {...layoutStyles} data-test-selector={fields.testSelectorId}>
            <StyledWrapper {...overlayWrapperStyles}>
                <StyledWrapper {...slideCenteringWrapperStyles}>
                    <Zone contentId={id} zoneName="containerZone" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const layoutTab = {
    displayName: "Layout",
    sortOrder: 0,
};

const widget: WidgetModule<Toolkit.FieldDefinition> = {
    component: StyledContainer,
    definition: {
        group: "Basic",
        displayName: "Styled Container",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.verticalAligment,
                displayName: "Content Vertical Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Bottom", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.horizontalAligment,
                displayName: "Content Horizontal Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.textAligment,
                displayName: "Overall Text Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Container Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "none" },
                    { displayName: "1/4 Viewport", value: "1/4" },
                    { displayName: "1/2 Viewport", value: "1/2" },
                    { displayName: "3/4 Viewport", value: "3/4" },
                    { displayName: "Full Viewport", value: "full" },
                    { displayName: "Custom", value: "custom" },
                ],
                defaultValue: "None",
                hideEmptyOption: true,
                tooltip: "The value is a percentage of the current viewport height",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.customMinHeight,
                displayName: "Custom Min Height",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: layoutTab,
                isVisible: item => item.fields.minimumHeight === "custom",
            },
            {
                fieldType: "General",
                name: fields.maxWidth,
                displayName: "Maximum Container Width",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Full Width", value: "fullWidth" },
                    { displayName: "Fit Content", value: "fitContent" },
                    { displayName: "Custom", value: "custom" },
                ],
                defaultValue: "fullWidth",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                name: fields.testSelectorId,
                displayName: "Test Selector ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.customMaxWidth,
                displayName: "Custom Max Width",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: layoutTab,
                isVisible: item => item.fields.maxWidth === "custom",
            },
            ...fieldDefinitionBuilder([
                {
                    fieldType: "General",
                    name: fields.background,
                    displayName: "Background",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Image", value: "image" },
                        { displayName: "Color", value: "color" },
                    ],
                    defaultValue: "",
                    hideEmptyOption: false,
                },
                {
                    fieldType: "Translatable",
                    name: fields.image,
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.background === "image",
                },
                {
                    fieldType: "General",
                    name: fields.hasImageOverlay,
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.background === "image",
                },
                {
                    fieldType: "General",
                    name: fields.imageOverlayType,
                    displayName: "Image Color Overlay",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Gradient", value: "gradient" },
                        { displayName: "Plain", value: "plain" },
                    ],
                    defaultValue: "gradient",
                    hideEmptyOption: false,
                    isVisible: widget => widget.fields.background === "image" && widget.fields.hasImageOverlay,
                },
                {
                    fieldType: "General",
                    name: fields.imageOverlayColor,
                    displayName: "Image Color Overlay",
                    editorTemplate: "ColorPickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.background === "image" && widget.fields.hasImageOverlay,
                },
                {
                    fieldType: "General",
                    name: fields.backgroundColor,
                    editorTemplate: "ColorPickerField",
                    displayName: "Color",
                    defaultValue: "black",
                    isVisible: widget => widget.fields.background === "color",
                },
                {
                    name: fields.hasBorder,
                    displayName: "Show Border?",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    fieldType: "General",
                },
                {
                    name: fields.borderColor,
                    editorTemplate: "ColorPickerField",
                    displayName: "Color",
                    defaultValue: "black",
                    fieldType: "General",
                    isVisible: widget => widget.fields.hasBorder,
                },
                {
                    name: fields.borderWidth,
                    displayName: "Border Width",
                    editorTemplate: "IntegerField",
                    defaultValue: 1,
                    fieldType: "General",
                    isVisible: widget => !widget.fields.isBorderWidthAdvancedOptions && widget.fields.hasBorder,
                },
                {
                    name: fields.isBorderWidthAdvancedOptions,
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    fieldType: "General",
                    isVisible: widget => widget.fields.hasBorder,
                },
                {
                    name: fields.borderWidthTop,
                    editorTemplate: "IntegerField",
                    defaultValue: 1,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
                },
                {
                    name: fields.borderWidthRight,
                    editorTemplate: "IntegerField",
                    defaultValue: 1,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
                },
                {
                    name: fields.borderWidthBottom,
                    editorTemplate: "IntegerField",
                    defaultValue: 1,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
                },
                {
                    name: fields.borderWidthLeft,
                    editorTemplate: "IntegerField",
                    defaultValue: 1,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
                },
                {
                    name: fields.borderRadius,
                    displayName: "Border Radius",
                    editorTemplate: "IntegerField",
                    defaultValue: 0,
                    fieldType: "General",
                    isVisible: widget => !widget.fields.isBorderRadiusAdvancedOptions,
                },
                {
                    name: fields.isBorderRadiusAdvancedOptions,
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    fieldType: "General",
                },
                {
                    name: fields.borderRadiusTopLeft,
                    editorTemplate: "IntegerField",
                    defaultValue: 0,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
                },
                {
                    name: fields.borderRadiusTopRight,
                    editorTemplate: "IntegerField",
                    defaultValue: 0,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
                },
                {
                    name: fields.borderRadiusBottomRight,
                    editorTemplate: "IntegerField",
                    defaultValue: 0,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
                },
                {
                    name: fields.borderRadiusBottomLeft,
                    editorTemplate: "IntegerField",
                    defaultValue: 0,
                    fieldType: "General",
                    isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
                },
                {
                    fieldType: "General",
                    name: fields.contentPadding,
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => !widget.fields.isPaddingAdvancedOptions,
                },
                {
                    fieldType: "General",
                    name: fields.isPaddingAdvancedOptions,
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    fieldType: "General",
                    name: fields.paddingTop,
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => widget.fields.isPaddingAdvancedOptions,
                },
                {
                    fieldType: "General",
                    name: fields.paddingRight,
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => widget.fields.isPaddingAdvancedOptions,
                },
                {
                    fieldType: "General",
                    name: fields.paddingBottom,
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => widget.fields.isPaddingAdvancedOptions,
                },
                {
                    fieldType: "General",
                    name: fields.paddingLeft,
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => widget.fields.isPaddingAdvancedOptions,
                },
            ]),
        ],
    },
};

export default widget;
